import React, { useState, useRef, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import styles from './TextInputTable.module.scss';
import { Icon } from '@mui/material';
import { Text } from '../typography';
import { keys } from '../../utilities/translator/translation_keys.js';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc.js';

const DEBOUNCE_MS = 700;

// Single source of truth for wire-format parsing: "c1\nc2|c1\nc2" → rows
const parseRows = (inputValue) => {
  if (!inputValue || typeof inputValue !== 'string') return null;
  return inputValue.split('|').map((rowStr, i) => ({
    id: i + 1,
    data: rowStr.split('\n'),
  }));
};

// Serialize rows → array passed to onChange; parent joins with "|" for storage
const serializeRows = (rows) => rows.map((row) => row.data.join('\n'));

const TextInputTable = ({
  headers,
  onChange,
  inputValue,
  title,
  prefillCandidates,
}) => {
  const online = useOnlineStatus();
  const headerCount = headers?.length ?? 1;

  const [rows, setRows] = useState(() => {
    const parsed = parseRows(inputValue);
    return parsed ?? [{ id: 1, data: new Array(headerCount).fill('') }];
  });
  const [editRowId, setEditRowId] = useState(inputValue ? null : 1);
  const [fullScreen, setFullScreen] = useState(false);

  const dirtyCells = useRef(new Set());
  const saveTimerRef = useRef(null);
  const rowsRef = useRef(rows);
  // Wire-string of our last onChange call; used to detect when inputValue echoes
  // our own write back so we don't clear dirty cells prematurely on a racing poll.
  const pendingSaveRef = useRef(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Sync server changes into non-dirty cells and handle row additions/deletions
  useEffect(() => {
    if (!online) return;

    // Our own write echoing back — safe to clear dirty tracking, no merge needed
    if (
      pendingSaveRef.current !== null &&
      inputValue === pendingSaveRef.current
    ) {
      dirtyCells.current.clear();
      pendingSaveRef.current = null;
      return;
    }

    const serverRows = parseRows(inputValue);
    if (!serverRows) {
      if (dirtyCells.current.size === 0) {
        setRows([{ id: 1, data: new Array(headerCount).fill('') }]);
      }
      return;
    }

    setRows((prev) => {
      const maxLen = Math.max(prev.length, serverRows.length);
      const result = [];

      for (let idx = 0; idx < maxLen; idx++) {
        const localRow = prev[idx];
        const serverRow = serverRows[idx];

        if (!serverRow) {
          // Server deleted this row — keep it only if the user has unsaved edits in it
          const rowHasDirty = localRow.data.some((_, colIdx) =>
            dirtyCells.current.has(`${localRow.id}-${colIdx}`),
          );
          if (rowHasDirty) result.push(localRow);
        } else if (!localRow) {
          // New row added by another user
          result.push({ id: idx + 1, data: serverRow.data });
        } else {
          // Merge: dirty cells keep local value; clean cells take server value
          const mergedData = localRow.data.map((cellValue, colIdx) =>
            dirtyCells.current.has(`${localRow.id}-${colIdx}`)
              ? cellValue
              : serverRow.data[colIdx] ?? cellValue,
          );
          result.push({ ...localRow, data: mergedData });
        }
      }

      // Re-assign IDs sequentially after any structural changes
      return result.map((row, i) => ({ ...row, id: i + 1 }));
    });
  }, [inputValue, online, headerCount]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const save = (currentRows) => {
    const serialized = serializeRows(currentRows);
    // Record the exact wire-string so the sync effect can identify our own echo
    pendingSaveRef.current = serialized.join('|');
    onChange(serialized);
    // dirty cells are NOT cleared here — cleared only when our echo comes back
  };

  const handleAddRow = () => {
    const newRow = {
      id: rows.length > 0 ? rows[rows.length - 1].id + 1 : 1,
      data: new Array(headerCount).fill(''),
    };
    const newRows = [...rows, newRow];
    rowsRef.current = newRows;
    setRows(newRows);
    setEditRowId(newRow.id);
  };

  const handleRemoveRow = (id) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setEditRowId(null);

    // Immutably filter and re-index
    const newRows = rows
      .filter((row) => row.id !== id)
      .map((row, i) => ({ ...row, id: i + 1 }));

    // Remap dirty-cell keys to match renumbered rows
    const updatedDirty = new Set();
    dirtyCells.current.forEach((key) => {
      const [rowIdStr, colIdxStr] = key.split('-');
      const dirtyRowId = parseInt(rowIdStr, 10);
      if (dirtyRowId < id) updatedDirty.add(key);
      else if (dirtyRowId > id)
        updatedDirty.add(`${dirtyRowId - 1}-${colIdxStr}`);
      // dirtyRowId === id: row is deleted, drop the dirty mark
    });
    dirtyCells.current = updatedDirty;

    rowsRef.current = newRows;
    setRows(newRows);
    save(newRows);
  };

  const handleInputChange = (e, id, index) => {
    e.preventDefault();
    const value = e.target.value.replace(/\n/g, ' ');

    setRows((prev) => {
      const newRows = prev.map((row) =>
        row.id === id
          ? {
              ...row,
              data: row.data.map((cell, i) => (i === index ? value : cell)),
            }
          : row,
      );
      rowsRef.current = newRows;
      return newRows;
    });

    dirtyCells.current.add(`${id}-${index}`);

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      save(rowsRef.current);
      saveTimerRef.current = null;
    }, DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    save(rowsRef.current);
  };

  const handleEditRow = (id) => {
    setEditRowId(id);
  };

  // Bulk-inserts one row per currently-triggered prefill candidate (already collapsed/deduped
  // by the caller) in a single save, rather than one insert per dropdown selection.
  const handleInsertPrefillRows = () => {
    if (!prefillCandidates?.length) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    let nextId = rows.length > 0 ? rows[rows.length - 1].id + 1 : 1;
    const insertedRows = prefillCandidates.map((candidate) => ({
      id: nextId++,
      data: headers.map((header) => candidate.data?.columns?.[header] ?? ''),
    }));
    const newRows = [...rows, ...insertedRows];
    rowsRef.current = newRows;
    setRows(newRows);
    save(newRows);
  };

  const { t } = useTranslation();

  const tableRef = useRef(null);

  const handleOutsideClick = (e) => {
    if (tableRef.current && !tableRef.current.contains(e.target)) {
      setTimeout(() => {
        setEditRowId(null);
      }, 100);
    }
  };

  useEffect(() => {
    if (rows[0]?.data[0])
      document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [rows]);

  const cellWidth = `${100 / headers.length}%`;

  return (
    <div
      ref={tableRef}
      className={fullScreen ? styles.fullScreen : styles.container}
    >
      {fullScreen && (
        <div className={styles.header}>
          <Text size="lg" weight="bold">
            {title}
          </Text>
          <Icon className={styles.hover} onClick={() => setFullScreen(false)}>
            close
          </Icon>
        </div>
      )}
      {prefillCandidates?.length > 0 && (
        <div className={styles.newRow} onClick={handleInsertPrefillRows}>
          <Icon
            baseClassName="material-icons-outlined"
            className={styles.addButton}
            sx={{ fontSize: '1rem' }}
          >
            auto_fix_high
          </Icon>
          <Text noMargin size="sm" color="accentPrimary" weight="semiBold">
            {t(keys.assessments.INSERT_PREFILL_ROWS)} (
            {prefillCandidates.length})
          </Text>
        </div>
      )}
      {prefillCandidates?.length > 0 && (
        <Text noMargin size="xs" color="orange">
          {t(keys.assessments.PREFILL_DISCLAIMER)}
        </Text>
      )}
      <div className={styles.tableContainer}>
        <Table striped bordered className={styles.table}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th className={styles.tableHeader} key={index}>
                  <Text size="sm" noMargin>
                    {header}
                  </Text>
                </th>
              ))}
              <th>
                <div className={styles.iconColumn}>
                  <Icon
                    sx={{ fontSize: '1.2rem' }}
                    onClick={() => {
                      setFullScreen(!fullScreen);
                    }}
                  >
                    {fullScreen ? 'fullscreen_exit' : 'fullscreen'}
                  </Icon>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((row) => (
              <tr key={row.id} onClick={() => handleEditRow(row.id)}>
                {row?.data.map((value, index) => (
                  <td
                    key={index}
                    className={styles.cell}
                    style={{ width: cellWidth, minWidth: '10rem' }}
                  >
                    {editRowId === row.id ? (
                      <textarea
                        className={styles.cellInput}
                        type="text"
                        rows={2}
                        value={value || ''}
                        onChange={(e) => handleInputChange(e, row.id, index)}
                        onBlur={handleBlur}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddRow();
                          }
                        }}
                      />
                    ) : (
                      <Text size="sm" noMargin noSelect>
                        {value}
                      </Text>
                    )}
                  </td>
                ))}
                <td>
                  <div className={styles.iconColumn}>
                    <Icon
                      className={
                        rows?.length > 1
                          ? styles.deleteButton
                          : styles.deleteButtonDisabled
                      }
                      sx={{
                        fontSize: '1rem',
                      }}
                      onClick={(e) => {
                        if (rows?.length > 1) {
                          e.stopPropagation();
                          setEditRowId(null);
                          handleRemoveRow(row.id);
                        }
                      }}
                    >
                      delete
                    </Icon>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className={styles.newRow} onClick={handleAddRow}>
        <Icon
          baseClassName="material-icons-outlined"
          className={styles.addButton}
          sx={{ fontSize: '1rem' }}
        >
          add_circle
        </Icon>
        <Text noMargin size="sm" color="accentPrimary" weight="semiBold">
          {t(keys.assessments.NEW_ROW)}
        </Text>
      </div>
    </div>
  );
};

export default TextInputTable;
